import React, { useEffect, useState, useRef } from 'react';
import { StructureEditorModal } from './StructureEditor';

// Shared SVG cache for molecule column headers (persists across re-renders)
const headerSvgCache = new Map<string, string>();

export interface MoleculeColumnHeaderParams {
  smiles: string;
  name: string | null;
  moleculeId: number;
  displayName?: string;
}

const MoleculeColumnHeader: React.FC<MoleculeColumnHeaderParams> = ({
  smiles,
  name,
  moleculeId,
}) => {
  const [editorOpen, setEditorOpen] = useState(false);
  const [svg, setSvg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!smiles) return;

    const cacheKey = smiles.substring(0, 100);
    const cached = headerSvgCache.get(cacheKey);
    if (cached) {
      setSvg(cached);
      return;
    }

    setLoading(true);
    const fetchSvg = async () => {
      try {
        const { default: apiClient } = await import('../../api/client');
        const response = await apiClient.post('/v2/indigo/render', {
          structure: { value: smiles, type: 'RAW' },
          width: '64px',
          height: '48px',
        }, {
          responseType: 'text',
        });
        const svgData = response.data;
        headerSvgCache.set(cacheKey, svgData);
        if (mountedRef.current) {
          setSvg(svgData);
        }
      } catch (err) {
        // Silently fail - show name/ID only
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchSvg();
  }, [smiles]);

  const label = name || `#${moleculeId}`;

  return (
    <>
    <div
      className="mol-col-header"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        padding: '4px 2px',
        overflow: 'hidden',
        cursor: 'pointer',
      }}
      title={`Double-click to view structure\n${label}\n${smiles}`}
      onDoubleClick={() => setEditorOpen(true)}
    >
      {/* Structure thumbnail */}
      <div
        style={{
          width: 64,
          height: 48,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {svg ? (
          <div
            dangerouslySetInnerHTML={{ __html: svg }}
            style={{ width: 64, height: 48, overflow: 'hidden' }}
          />
        ) : loading ? (
          <div
            style={{
              width: 40,
              height: 32,
              borderRadius: 4,
              background: 'linear-gradient(90deg, var(--hover-bg) 25%, var(--card-border) 50%, var(--hover-bg) 75%)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 1.5s infinite',
            }}
          />
        ) : (
          <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>--</div>
        )}
      </div>
      {/* Label */}
      <div
        style={{
          fontSize: 10,
          lineHeight: '13px',
          color: 'var(--text-primary)',
          textAlign: 'center',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
          marginTop: 2,
        }}
      >
        {label.length > 10 ? label.substring(0, 9) + '\u2026' : label}
      </div>
    </div>
    {editorOpen && (
      <StructureEditorModal
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        initialStructure={smiles}
        title={label || 'View Structure'}
        moleculeName={name || undefined}
      />
    )}
    </>
  );
};

export default MoleculeColumnHeader;
